import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const INK    = '#0d0d0d';
const BLUE   = '#1755f4';
const BORDER = '#e8e8e8';
const MUTED  = '#888';
const FONT   = "'Helvetica Neue', Helvetica, Arial, sans-serif";

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtEur(v) {
  if (!v && v !== 0) return '–';
  return Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

const STATUS_LABEL = { paid: 'Bezahlt', pending: 'Offen', partial: 'Teilweise', overdue: 'Überfällig' };
const STATUS_BG    = { paid: '#f0fdf4', pending: '#fffbeb', partial: '#f5f5f5', overdue: '#fef2f2' };
const STATUS_COLOR = { paid: '#16a34a', pending: '#d97706', partial: '#111', overdue: '#dc2626' };
const LEVEL_LABEL  = { 1: 'Zahlungserinnerung', 2: '1. Mahnung', 3: '2. Mahnung' };

export default function MieterDashboard() {
  const { user, profile, signOut } = useAuth();
  const [tenant, setTenant]           = useState(null);
  const [payments, setPayments]       = useState([]);
  const [reminders, setReminders]     = useState([]);
  const [requests, setRequests]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [reqForm, setReqForm]         = useState({ type: 'general', title: '', description: '' });
  const [sending, setSending]         = useState(false);
  const [reqSuccess, setReqSuccess]   = useState(false);
  const [markingId, setMarkingId]     = useState(null);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    setLoading(true);

    // Find tenant record linked to this user (no leases join)
    const { data: tenantData } = await supabase
      .from('tenants')
      .select('id, owner_id, portal_user_id, first_name, last_name, email, unit, monthly_rent, rent, lease_start, lease_end, property_id')
      .eq('portal_user_id', user.id)
      .single();

    if (!tenantData) { setLoading(false); return; }
    setTenant(tenantData);

    // Payments directly via tenant_id (no lease_id)
    const { data: payData } = await supabase
      .from('rent_payments')
      .select('id, due_date, amount_due, amount_paid, status, paid_date, notes')
      .eq('tenant_id', tenantData.id)
      .order('due_date', { ascending: false })
      .limit(12);
    setPayments(payData || []);

    // Reminders for this tenant's payments
    if (payData?.length) {
      const payIds = payData.map(p => p.id);
      const { data: remData } = await supabase
        .from('reminders')
        .select('id, rent_payment_id, level, status, due_date, amount, fee, sent_at')
        .in('rent_payment_id', payIds)
        .order('sent_at', { ascending: false });
      setReminders(remData || []);
    }

    // Requests
    const { data: reqData } = await supabase
      .from('tenant_requests')
      .select('id, type, title, description, status, created_at')
      .eq('tenant_id', tenantData.id)
      .order('created_at', { ascending: false });
    setRequests(reqData || []);

    setLoading(false);
  }

  async function markAsPaid(payment) {
    setMarkingId(payment.id);
    const { error } = await supabase
      .from('rent_payments')
      .update({ status: 'paid', amount_paid: payment.amount_due, paid_date: new Date().toISOString().split('T')[0] })
      .eq('id', payment.id);
    if (!error) {
      setPayments(prev => prev.map(p => p.id === payment.id
        ? { ...p, status: 'paid', amount_paid: payment.amount_due, paid_date: new Date().toISOString().split('T')[0] }
        : p
      ));
    }
    setMarkingId(null);
  }

  async function submitRequest(e) {
    e.preventDefault();
    if (!tenant) return;
    setSending(true);
    await supabase.from('tenant_requests').insert({
      tenant_id:   tenant.id,
      owner_id:    tenant.owner_id,
      type:        reqForm.type,
      title:       reqForm.title,
      description: reqForm.description,
    });
    await supabase.from('notifications').insert({
      owner_id: tenant.owner_id,
      title:    'Neue Mieteranfrage',
      body:     `${tenant.first_name} ${tenant.last_name}: ${reqForm.title}`,
      link:     '/requests',
    });
    setReqForm({ type: 'general', title: '', description: '' });
    setReqSuccess(true);
    setTimeout(() => setReqSuccess(false), 4000);
    await fetchData();
    setSending(false);
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED }}>Lädt…</span>
    </div>
  );

  const firstName = profile?.first_name || tenant?.first_name || '';
  const h = new Date().getHours();
  const greeting = h < 12 ? 'Guten Morgen' : h < 18 ? 'Guten Tag' : 'Guten Abend';

  // Current month payment
  const todayStr = new Date().toISOString().split('T')[0];
  const monthPrefix = todayStr.slice(0, 7);
  const currentPayment = payments.find(p => p.due_date?.startsWith(monthPrefix));
  const monthlyRent = Number(tenant?.monthly_rent || tenant?.rent || 0);

  // Upcoming/open reminders
  const activeReminders = reminders.filter(r => r.status === 'sent' || r.status === 'pending');

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: FONT }}>

      {/* Nav */}
      <nav style={{
        background: INK, padding: '0 32px', height: '52px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <span style={{ fontSize: '12px', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: 'white' }}>
          IMMO<span style={{ color: BLUE }}>HQ</span>
          <span style={{ marginLeft: '10px', fontSize: '8px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)' }}>MIETERPORTAL</span>
        </span>
        <button
          onClick={signOut}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', padding: '6px 14px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
        >
          Abmelden
        </button>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px' }}>

        {/* Greeting header */}
        <div style={{ background: INK, padding: '32px 36px', marginBottom: '24px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' }}>
            {greeting}
          </div>
          <h1 style={{ fontSize: '36px', fontWeight: 900, letterSpacing: '-1.5px', color: 'white', margin: '0 0 20px' }}>
            {firstName ? `${firstName}.` : 'Willkommen.'}
          </h1>
          {tenant && (
            <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
              {[
                { label: 'Einheit',    value: tenant.unit || '–' },
                { label: 'Kaltmiete', value: fmtEur(monthlyRent) },
                { label: 'Mietbeginn', value: fmt(tenant.lease_start) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: '4px' }}>{label}</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: 'white' }}>{value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!tenant ? (
          <div style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '48px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: MUTED }}>Dein Mieterkonto ist noch nicht mit einem Mietverhältnis verknüpft.</p>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>Bitte wende dich an deinen Vermieter.</p>
          </div>
        ) : (
          <>
            {/* ── Current month status card ── */}
            <div style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '24px 28px', marginBottom: '24px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '16px' }}>
                Aktuelle Miete — {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
              </div>
              {currentPayment ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '36px', fontWeight: 900, letterSpacing: '-1.5px', color: STATUS_COLOR[currentPayment.status] || INK, lineHeight: 1 }}>
                      {fmtEur(currentPayment.amount_due)}
                    </div>
                    <div style={{ fontSize: '13px', color: MUTED, marginTop: '6px' }}>
                      Fällig: {fmt(currentPayment.due_date)}
                      {currentPayment.amount_paid > 0 && currentPayment.status !== 'paid' &&
                        ` · ${fmtEur(currentPayment.amount_paid)} bezahlt`
                      }
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '5px 14px',
                      background: STATUS_BG[currentPayment.status], color: STATUS_COLOR[currentPayment.status],
                      border: `1px solid ${STATUS_COLOR[currentPayment.status]}30`,
                      letterSpacing: '0.5px',
                    }}>
                      {STATUS_LABEL[currentPayment.status] || currentPayment.status}
                    </span>
                    {currentPayment.status !== 'paid' && (
                      <button
                        onClick={() => markAsPaid(currentPayment)}
                        disabled={markingId === currentPayment.id}
                        style={{
                          background: markingId === currentPayment.id ? MUTED : INK,
                          color: 'white', border: 'none',
                          padding: '8px 18px', fontSize: '11px', fontWeight: 700,
                          letterSpacing: '0.5px', cursor: markingId === currentPayment.id ? 'not-allowed' : 'pointer',
                          fontFamily: FONT,
                        }}
                      >
                        {markingId === currentPayment.id ? 'Bitte warten…' : 'Als bezahlt markieren'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: MUTED }}>
                  Für diesen Monat liegt noch keine Zahlung vor.
                </div>
              )}
            </div>

            {/* ── Active reminders ── */}
            {activeReminders.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#b91c1c', marginBottom: '12px' }}>
                  Offene Mahnungen
                </div>
                <div style={{ background: 'white', border: '1.5px solid #fecaca' }}>
                  {activeReminders.map((r, i) => {
                    const relPayment = payments.find(p => p.id === r.rent_payment_id);
                    return (
                      <div key={r.id} style={{
                        padding: '16px 20px',
                        borderBottom: i < activeReminders.length - 1 ? `1px solid ${BORDER}` : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                      }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: INK }}>{LEVEL_LABEL[r.level] || `Stufe ${r.level}`}</div>
                          <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>
                            {r.sent_at ? `Gesendet: ${fmt(r.sent_at)}` : `Zahlungsfrist: ${fmt(r.due_date)}`}
                            {relPayment ? ` · Für ${fmt(relPayment.due_date)}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#b91c1c' }}>
                            {fmtEur((r.amount || 0) + (r.fee || 0))}
                          </div>
                          {r.fee > 0 && <div style={{ fontSize: '10px', color: MUTED }}>inkl. {fmtEur(r.fee)} Gebühr</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Zahlungsübersicht ── */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '12px' }}>
                Zahlungsübersicht
              </div>
              <div style={{ background: 'white', border: `1px solid ${BORDER}` }}>
                {payments.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: MUTED }}>Keine Zahlungen vorhanden</div>
                ) : payments.map((p, i) => (
                  <div key={p.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 20px', borderBottom: i < payments.length - 1 ? `1px solid ${BORDER}` : 'none',
                    gap: '12px',
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: INK }}>{fmt(p.due_date)}</div>
                      <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{fmtEur(p.amount_due)}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {p.amount_paid > 0 && p.status !== 'paid' && (
                        <span style={{ fontSize: '11px', color: MUTED }}>{fmtEur(p.amount_paid)} bezahlt</span>
                      )}
                      {p.status !== 'paid' && (
                        <button
                          onClick={() => markAsPaid(p)}
                          disabled={markingId === p.id}
                          style={{
                            background: 'none', border: `1px solid ${BORDER}`, color: INK,
                            padding: '5px 12px', fontSize: '10px', fontWeight: 700,
                            cursor: markingId === p.id ? 'not-allowed' : 'pointer', fontFamily: FONT,
                            letterSpacing: '0.5px',
                          }}
                        >
                          {markingId === p.id ? '…' : 'Als bezahlt markieren'}
                        </button>
                      )}
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '3px 10px',
                        background: STATUS_BG[p.status], color: STATUS_COLOR[p.status],
                        letterSpacing: '0.5px',
                      }}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Request form ── */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '12px' }}>
                Anfrage stellen
              </div>
              <div style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '24px' }}>
                {reqSuccess && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 16px', marginBottom: '16px', fontSize: '13px', color: '#15803d' }}>
                    Anfrage gesendet.
                  </div>
                )}
                <form onSubmit={submitRequest}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' }}>Typ</label>
                      <select
                        value={reqForm.type}
                        onChange={e => setReqForm(f => ({ ...f, type: e.target.value }))}
                        style={{ width: '100%', border: `1px solid ${BORDER}`, padding: '10px 12px', fontSize: '13px', color: INK, fontFamily: FONT, outline: 'none', background: 'white' }}
                      >
                        <option value="general">Allgemein</option>
                        <option value="maintenance">Reparatur / Instandhaltung</option>
                        <option value="document">Dokument anfordern</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' }}>Betreff</label>
                      <input
                        type="text"
                        required
                        value={reqForm.title}
                        onChange={e => setReqForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="Kurze Beschreibung"
                        style={{ width: '100%', border: `1px solid ${BORDER}`, padding: '10px 12px', fontSize: '13px', color: INK, fontFamily: FONT, outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' }}>Details</label>
                    <textarea
                      value={reqForm.description}
                      onChange={e => setReqForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Beschreibe dein Anliegen…"
                      rows={4}
                      style={{ width: '100%', border: `1px solid ${BORDER}`, padding: '10px 12px', fontSize: '13px', color: INK, fontFamily: FONT, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    style={{ background: sending ? MUTED : INK, color: 'white', border: 'none', padding: '11px 24px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: sending ? 'not-allowed' : 'pointer', fontFamily: FONT }}
                  >
                    {sending ? 'Sendet…' : 'Anfrage senden'}
                  </button>
                </form>
              </div>
            </div>

            {/* ── Previous requests ── */}
            {requests.length > 0 && (
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '12px' }}>
                  Meine Anfragen
                </div>
                <div style={{ background: 'white', border: `1px solid ${BORDER}` }}>
                  {requests.map((r, i) => (
                    <div key={r.id} style={{
                      padding: '14px 20px', borderBottom: i < requests.length - 1 ? `1px solid ${BORDER}` : 'none',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
                    }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: INK }}>{r.title}</div>
                        <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{fmt(r.created_at)}</div>
                      </div>
                      <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1px', padding: '3px 8px', border: `1px solid ${BORDER}`, color: MUTED, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {r.status === 'open' ? 'Offen' : r.status === 'in_progress' ? 'In Bearbeitung' : r.status === 'resolved' ? 'Erledigt' : 'Geschlossen'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
